import { useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useAgentSubmit } from '../../api/hooks/agent';
import { useWatchlist, WATCH_TYPES } from '../../api/hooks/watchlist';
import { resetAgentForm, selectWatchlistForm } from '../../state/agentSlice';

const TYPE_NOUN = {
  [WATCH_TYPES.fund]: 'fund',
  [WATCH_TYPES.stock]: 'stock',
  [WATCH_TYPES.portfolio]: 'model portfolio',
};

/** The runner's own sentence, with the backend's appended when it said one. */
const withMessage = (base, message) => (message ? `${base}; ${message}` : base);

/** The mutation's own sentence, when it gave one, trimmed for a one-line report. */
const said = (res) => String(res?.message ?? '').replace(/\s+/g, ' ').trim().slice(0, 200);

/**
 * Handlers for the agent signals that belong to no screen. Renders nothing.
 *
 * Adding a fund to the watchlist does not need the reader to be looking at the
 * watchlist, so `REQUEST_WATCHLIST_SUBMIT` is answered here, in the shell,
 * which is mounted on every screen. The agent fills `agent.watchlistForm` with
 * `UPDATE_WATCHLIST_DATA` (`securitySymbol`, `securityType`, `op`) first; the
 * slice has already dropped any type or op it does not know.
 *
 * The form is read from the store when the request runs, not from the last
 * render: the update and the signal usually arrive in the same envelope, and
 * the request can start before React has rendered the update. It is used up by
 * that read, whatever the outcome: there is no confirmation card here, and an
 * `op: 'remove'` or a `securityType` left behind by a request that failed would
 * otherwise apply to the next one — an "add" that relied on the defaults would
 * run as an unconfirmed remove.
 *
 * The watchlist's list query is skipped. This component lives on every page,
 * and a network read of the whole list on every app load — for an action most
 * sessions never use — is not worth it. The writes still work without it, and
 * each one refetches the list, which refreshes any watchlist view that is on
 * screen. The cost is that "already on the list" is learned from the backend's
 * answer rather than checked first, which the handler accounts for.
 */
export default function AgentGlobalActions() {
  const store = useStore();
  const dispatch = useDispatch();
  const { add, remove } = useWatchlist({ skip: true });

  const onSubmit = useCallback(async () => {
    const form = selectWatchlistForm(store.getState());
    dispatch(resetAgentForm('watchlistForm'));
    const removing = form.op === 'remove';
    const op = removing ? 'removeFromWatchlist' : 'addToWatchlist';
    const symbol = form.securitySymbol;
    if (!symbol) {
      return { ok: false, op, error: 'no security to change — send UPDATE_WATCHLIST_DATA with securitySymbol first' };
    }
    const type = form.securityType || WATCH_TYPES.fund;
    const noun = TYPE_NOUN[type] || 'security';
    /* The list named, else the default; a remove with no list clears the name from every list. */
    const listName = String(form.listName ?? '').trim() || null;
    const where = listName ? `the "${listName}" watchlist` : removing ? 'the watchlist' : 'the General watchlist';

    const res = removing ? await remove(symbol, type, { listName }) : await add(symbol, type, {}, { listName });
    const message = said(res);
    const done = (detail) => ({ ok: true, op, detail, data: { securitySymbol: symbol, securityType: type, listName: listName || 'General' } });

    if (res?.success) {
      return done(withMessage(`${symbol} (${noun}) ${removing ? 'removed from' : 'added to'} ${where}`, message));
    }
    if (!removing) {
      /* A duplicate add is the state the agent asked for, reached earlier. Said
         as success so the agent does not apologise for a watchlist that is right. */
      if (/already/i.test(message)) return done(`${symbol} (${noun}) was already on ${where}`);
      return { ok: false, op, error: message || `the watchlist did not accept ${symbol}` };
    }
    if (/not (found|(in|on) (the |your )?watch ?list)|does not exist|no such/i.test(message)) {
      return { ok: false, op, error: `${symbol} (${noun}) is not on the watchlist` };
    }
    return { ok: false, op, error: message || `could not remove ${symbol} from the watchlist` };
  }, [add, remove, dispatch, store]);

  useAgentSubmit(null, 'REQUEST_WATCHLIST_SUBMIT', onSubmit);

  return null;
}
